
/**
 * Module dependencies.
 */

import { Circles } from './circles';
import { ComponentType } from 'react';
import { Spinner } from './spinner';
import { ifNotProp, ifProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * `LoadingType` type.
 */

type LoadingType = 'circles' | 'spinner';

/**
 * `Props` type.
 */

type Props = {
  active: boolean;
  className?: string;
  relative?: boolean;
  size?: number;
  stroke?: number;
  type?: LoadingType;
};

/**
 * Get loader component.
 */

function getLoaderComponent(type: LoadingType): ComponentType<any> {
  switch (type) {
    case 'circles':
      return Circles;

    default:
      return Spinner;
  }
}

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.span<{ active: boolean; relative: boolean }>`
  display: inline-block;
  opacity: ${ifProp('active', 1, 0)};
  transition: opacity ${theme('animations.defaultTransition')};

  ${ifNotProp('relative', css`
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
  `)}
`;

/**
 * Export `Loading` component.
 */

export function Loading(props: Props): JSX.Element {
  const { active, className, relative = false, type = 'spinner', ...rest } = props;
  const Loader = getLoaderComponent(type);

  return (
    <Wrapper
      active={active}
      className={className}
      relative={relative}
    >
      <Loader
        {...rest}
        active={active}
      />
    </Wrapper>
  );
}
